import { useMemo } from 'react'
import { BarChartWrapper } from '../charts/BarChart'
import { formatMoney } from '../format'
import { buildDrillRsql } from '../drill'
import { WidgetFrame } from './WidgetFrame'
import { useWidgetData } from './useWidgetData'
import { WIDGET_KEY, type WidgetProps } from './types'
import { runExport, type ExportColumn } from '../../../lib/export'
import { label } from '../../../lib/labels'
import type { CreditValueBucket, CreditValueByJurisdictionResult } from '../../../types/insights'

const WIDGET_KEY_SELF = WIDGET_KEY.creditValueByJurisdictionFy

/**
 * Every certified-value fact at launch is USD (GA/NY/LA + US-Fed all certify
 * in USD today) — the backend's `CreditValueAssembler.assemble` is called
 * with a fixed `reportingCurrency="USD"` (AC2 footnote); the wire payload
 * carries no `currency` field of its own, so this is a disclosed constant,
 * never a guess.
 */
const REPORTING_CURRENCY = 'USD'

/** Reporting & Exports — the CSV mirrors the widget's own row shape (jurisdiction/FY/certified amount), business-labelled, never raw keys. */
const CREDIT_VALUE_EXPORT_COLUMNS: ExportColumn<CreditValueBucket>[] = [
  { id: 'jurisdiction', header: 'Jurisdiction', toText: (b) => label('jurisdiction', b.jurisdiction) },
  { id: 'fiscalYear', header: 'Fiscal year', toText: (b) => fiscalYearLabel(b.fiscalYearId) },
  { id: 'certifiedAmount', header: `Certified amount (${REPORTING_CURRENCY})`, toText: (b) => formatMoney(b.sumCertifiedAmount, REPORTING_CURRENCY) },
]

interface PivotedRow {
  fiscalYear: string
  [jurisdiction: string]: string | number
}

/**
 * No `config.api.FiscalYearApi.get(UUID)` label lookup exists yet (disclosed
 * gap — same posture as `IB-157`/`DashboardFilterFields`'s raw-ID inputs), so
 * the raw id is the best label available. `null` means a zero-fill
 * jurisdiction row with no filing yet (AC25) — rendered as "Unfiled" rather
 * than a blank/undefined axis tick.
 */
function fiscalYearLabel(fiscalYearId: string | null): string {
  return fiscalYearId ?? 'Unfiled'
}

function pivotByFiscalYear(buckets: CreditValueBucket[]): { rows: PivotedRow[]; series: { key: string; label: string }[] } {
  const fyOrder: string[] = []
  const jurisdictions = new Set<string>()
  const byFy = new Map<string, Record<string, number>>()

  for (const b of buckets) {
    const fyKey = fiscalYearLabel(b.fiscalYearId)
    if (!byFy.has(fyKey)) {
      byFy.set(fyKey, {})
      fyOrder.push(fyKey)
    }
    jurisdictions.add(b.jurisdiction)
    byFy.get(fyKey)![b.jurisdiction] = Number(b.sumCertifiedAmount)
  }

  const rows: PivotedRow[] = fyOrder.map((fyKey) => ({ fiscalYear: fyKey, ...byFy.get(fyKey) }))
  const series = [...jurisdictions]
    .sort((a, b) => a.localeCompare(b))
    .map((key) => ({ key, label: key }))
  return { rows, series }
}

/**
 * AC2/AC25 — Credit value by jurisdiction/FY (hero tile 1 of 3). Zero-filing
 * jurisdictions render at $0.
 *
 * IB-162 fix: the endpoint's `data` is `{ buckets, excludedCount }`
 * (`CreditValueByJurisdictionResult`), never a bare `CreditValueBucket[]` —
 * consuming it as a bare array threw (`buckets is not iterable`) inside this
 * widget's own render, white-screening the whole dashboard (no error
 * boundary existed to contain it). Both the shape and every field name
 * (`sumCertifiedAmount`, not `amount`; no `jurisdictionLabel`/
 * `fiscalYearLabel`/`currency`) now mirror the real backend contract.
 * `excludedCount` (non-USD facts the backend couldn't FX-resolve) surfaces
 * as an honest note rather than being silently dropped.
 */
export function CreditValueByJurisdictionFyWidget({ filter, skipRefetch, onCrossFilter, onDrill }: WidgetProps) {
  const { status, data, message, retry } = useWidgetData<CreditValueByJurisdictionResult>(WIDGET_KEY_SELF, filter, skipRefetch)

  const excludedCount = typeof data?.excludedCount === 'number' && data.excludedCount > 0 ? data.excludedCount : 0

  // Defensive against a malformed/unexpected payload (e.g. a future API
  // drift, or a bare array by accident) — degrade to the empty state,
  // never throw during render.
  const buckets = useMemo(() => (Array.isArray(data?.buckets) ? data.buckets : []), [data])
  const pivoted = useMemo(() => pivotByFiscalYear(buckets), [buckets])

  const frameState =
    status === 'ok' && pivoted.rows.length === 0 ? 'empty' : status === 'loading' ? 'loading' : status

  return (
    <WidgetFrame
      title="Credit value by jurisdiction / FY"
      icon="bar-chart"
      state={frameState}
      message={message}
      onRetry={retry}
      drill={{ label: 'View filings', onClick: () => onDrill(buildDrillRsql(filter)) }}
      exportAction={{
        onClick: () => runExport('csv', 'Credit value by jurisdiction-FY', buckets, CREDIT_VALUE_EXPORT_COLUMNS),
      }}
    >
      <BarChartWrapper<PivotedRow>
        title="Credit value by jurisdiction / FY"
        data={pivoted.rows}
        categoryKey="fiscalYear"
        categoryLabel="Fiscal year"
        series={pivoted.series}
        valueFormatter={(v) => formatMoney(String(v), REPORTING_CURRENCY)}
        onBarClick={(_row, seriesKey) => onCrossFilter('jurisdiction', seriesKey)}
      />
      {excludedCount > 0 ? (
        <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 'var(--iw-space-2) 0 0' }}>
          {excludedCount} certified value{excludedCount === 1 ? '' : 's'} excluded — non-{REPORTING_CURRENCY}{' '}
          currency, no FX rate applied.
        </p>
      ) : null}
    </WidgetFrame>
  )
}
